import { useEffect, RefObject } from 'react';
import { setFocusMode, LeaveFormHandler } from 't-lib-ui/structure';
import { findNearestInPostView } from './HeadlineListFocus';

function isVisible(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  return style.display != 'none' && style.visibility != 'hidden' && style.opacity != '0';
}

function isFullyVisible(el: HTMLElement, containerRect: DOMRect): boolean {
  const r = el.getBoundingClientRect();
  return r.top >= containerRect.top && r.bottom <= containerRect.bottom && r.height > 0;
}

function isPartiallyVisible(el: HTMLElement, containerRect: DOMRect): boolean {
  const r = el.getBoundingClientRect();
  return r.bottom > containerRect.top && r.top < containerRect.bottom && r.height > 0;
}

function hasVisibleModal(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>('.flowFormModal'))
    .some(el => el.offsetParent || el.matches(':modal'));
}

function getNonSidewayItems(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('.flowFocussable:not(.sidewayFocus)'))
    .filter(el => isVisible(el));
}

function findNearestInHeadlineList(sourceEl: HTMLElement): HTMLElement | null {
  const headlineList = document.querySelector('.headlineList') as HTMLElement | null;
  if (!headlineList) return null;

  const headlineListRect = headlineList.getBoundingClientRect();
  if (headlineListRect.width <= 0) return null;

  const candidates = Array.from(headlineList.querySelectorAll<HTMLElement>('.flowFocussable'))
    .filter(el => isVisible(el) && isPartiallyVisible(el, headlineListRect));

  if (!candidates.length) return null;

  const sourceRect = sourceEl.getBoundingClientRect();
  const sourceCenter = (sourceRect.top + sourceRect.bottom) / 2;

  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const el of candidates) {
    const r = el.getBoundingClientRect();
    const center = (r.top + r.bottom) / 2;
    const dist = Math.abs(center - sourceCenter);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  return best;
}

/** Find the effective non-sideway item for a focused element (itself, or its parent if sidewayFocus) */
function findEffectiveItem(focused: HTMLElement, sidebar: HTMLElement): HTMLElement {
  if (!focused.classList.contains('sidewayFocus')) return focused;

  // Try ancestor
  const ancestor = focused.closest('.flowFocussable:not(.sidewayFocus)') as HTMLElement | null;
  if (ancestor && sidebar.contains(ancestor)) return ancestor;

  // Try sibling in parent chain (walk up to handle wrapper divs)
  let parent = focused.parentElement;
  while (parent && sidebar.contains(parent)) {
    const siblings = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .flowFocussable:not(.sidewayFocus)'))
      .filter(el => isVisible(el));
    if (siblings.length) return siblings[0];
    parent = parent.parentElement;
  }

  return focused;
}

export function useLeftSidebarFocus(sidebarRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const scrollEl = sidebarRef.current;
    if (!scrollEl) return;
    // Use the outer .leftSidebar (which includes the footer) for focus containment
    // and item lookup. sidebarRef points to .sidebarBody, which would exclude footer buttons.
    const sidebar = (scrollEl.closest('.leftSidebar') as HTMLElement | null) ?? scrollEl;

    function mouseHandler() {
      setFocusMode('hidden');
    }

    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.defaultPrevented) return;
      if (hasVisibleModal()) return;

      const focused = document.activeElement as HTMLElement;

      // Only handle if focus is within this sidebar
      if (!sidebar!.contains(focused)) return;

      const key = ev.key;
      if (key != 'ArrowDown' && key != 'ArrowUp' && key != 'ArrowLeft' && key != 'ArrowRight' && key != 'Enter') return;

      setFocusMode('visible');

      // Text inputs in the sidebar (feed-name filter, folder create/rename): Left/Right must move
      // the text caret, not change focus. This handler runs in the capture phase — before the
      // input's own keydown — so without this guard every arrow press would steal focus. Only act
      // once the caret is at the matching edge with no selection; otherwise let the input have it.
      const textInput = (focused.tagName == 'INPUT' || focused.tagName == 'TEXTAREA') ? focused as HTMLInputElement : null;
      if (textInput && (key == 'ArrowLeft' || key == 'ArrowRight')) {
        const collapsed = textInput.selectionStart == textInput.selectionEnd;
        if (key == 'ArrowLeft' && !(collapsed && textInput.selectionStart == 0)) return;
        if (key == 'ArrowRight' && !(collapsed && textInput.selectionStart == textInput.value.length)) return;
        // Caret sits at the edge with no selection. The feed filter steps to its clear button at the
        // right edge (and falls through at the left); other inputs (rename/create) keep focus rather
        // than jumping out of the field mid-edit.
        if (!focused.classList.contains('feedFilterInput')) return;
        if (key == 'ArrowRight') {
          const clear = focused.closest('.sectionHeader')?.querySelector<HTMLElement>('.feedFilterClear');
          if (clear) {
            ev.preventDefault();
            ev.stopPropagation();
            clear.focus({ preventScroll: true });
          }
          return;
        }
        // feedFilterInput, ArrowLeft at the start: nothing to the left, fall through.
      }

      if (key == 'ArrowDown' || key == 'ArrowUp') {
        const items = getNonSidewayItems(sidebar!);
        const effective = findEffectiveItem(focused, sidebar!);
        const idx = items.indexOf(effective);
        const dir = key == 'ArrowDown' ? 1 : -1;
        const next = idx >= 0 ? items[idx + dir] : (dir == 1 ? items[0] : null);

        ev.preventDefault();
        ev.stopPropagation();
        if (next) next.focus();
        return;
      }

      if (key == 'Enter') {
        const tag = focused.tagName;
        if (focused.classList.contains('flowFocussable') && (tag == 'DIV' || tag == 'A')) {
          ev.preventDefault();
          ev.stopPropagation();
          focused.click();
        }
        return;
      }

      if (key == 'ArrowRight') {
        ev.preventDefault();
        ev.stopPropagation();

        // If on a sidewayFocus element, find next sideway sibling or go cross-panel
        if (focused.classList.contains('sidewayFocus')) {
          const parent = focused.parentElement;
          if (parent) {
            const sideways = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .sidewayFocus.flowFocussable'));
            const sidx = sideways.indexOf(focused);
            const next = sideways[sidx + 1];
            if (next) { next.focus({ preventScroll: true }); return; }
          }
          // Within a section header, sideway action buttons can live at different
          // nesting levels (Add feed is a direct child; Filter/options sit in a
          // wrapper). Step to the next sideway button anywhere in the header.
          const header = focused.closest('.sectionHeader');
          if (header && sidebar!.contains(header)) {
            const headerSideways = Array.from(header.querySelectorAll<HTMLElement>('.sidewayFocus.flowFocussable')).filter(isVisible);
            const hidx = headerSideways.indexOf(focused);
            const next = headerSideways[hidx + 1];
            if (next) { next.focus({ preventScroll: true }); return; }
          }
          // Within a feed/flat row, sideway buttons sit at different nesting levels (the
          // status badge is a direct child; the context menu is wrapped). Step to the next
          // sideway button anywhere in the row before going cross-panel.
          const row = focused.closest('.feedItem, .flatItem');
          if (row && sidebar!.contains(row)) {
            const rowSideways = Array.from(row.querySelectorAll<HTMLElement>('.sidewayFocus.flowFocussable')).filter(isVisible);
            const ridx = rowSideways.indexOf(focused);
            const next = rowSideways[ridx + 1];
            if (next) { next.focus({ preventScroll: true }); return; }
          }
          const target = findNearestInHeadlineList(focused) ?? findNearestInPostView(focused) ?? findNearestInMainContent(focused);
          if (target) target.focus({ preventScroll: true });
          return;
        }

        // On a regular item: look for a sidewayFocus child of the focused element itself.
        // Don't fall back to parent.querySelectorAll — that picks up sideway buttons on
        // sibling items (e.g. focusing Random and finding Unread Posts' context menu).
        const sidewayTarget = focused.querySelector<HTMLElement>('.sidewayFocus.flowFocussable');
        if (sidewayTarget) {
          sidewayTarget.focus({ preventScroll: true });
          return;
        }

        // Section-header action buttons (Add feed -> Filter -> options menu) are
        // siblings of the focused button, not descendants. Step into the first
        // sideway button in the same header so the row is keyboard-reachable.
        const header = focused.closest('.sectionHeader');
        if (header && sidebar!.contains(header)) {
          const headerSideway = Array.from(header.querySelectorAll<HTMLElement>('.sidewayFocus.flowFocussable')).find(el => isVisible(el));
          if (headerSideway) { headerSideway.focus({ preventScroll: true }); return; }
        }

        // Footer buttons are flat siblings (not nested), so step to the next one before going cross-panel.
        if (focused.classList.contains('sidebarFooterButton')) {
          const buttons = Array.from(focused.parentElement?.querySelectorAll<HTMLElement>(':scope > .sidebarFooterButton.flowFocussable') ?? []);
          const next = buttons[buttons.indexOf(focused) + 1];
          if (next) { next.focus({ preventScroll: true }); return; }
        }

        // No sideway target, go cross-panel (HeadlineList, PostView, or main content FlowForm)
        const target = findNearestInHeadlineList(focused) ?? findNearestInPostView(focused) ?? findNearestInMainContent(focused);
        if (target) target.focus({ preventScroll: true });
        return;
      }

      if (key == 'ArrowLeft') {
        ev.preventDefault();
        ev.stopPropagation();

        // If on a sidewayFocus element, go back to parent/sibling non-sideway item
        if (focused.classList.contains('sidewayFocus')) {
          // Check for preceding sideway sibling first
          const parent = focused.parentElement;
          if (parent) {
            const sideways = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .sidewayFocus.flowFocussable'));
            const sidx = sideways.indexOf(focused);
            if (sidx > 0) { sideways[sidx - 1].focus({ preventScroll: true }); return; }
          }

          // Mirror of the ArrowRight header-wide step: move to the previous sideway
          // button anywhere in the same section header before falling back to the
          // non-sideway header item.
          const header = focused.closest('.sectionHeader');
          if (header && sidebar!.contains(header)) {
            const headerSideways = Array.from(header.querySelectorAll<HTMLElement>('.sidewayFocus.flowFocussable')).filter(isVisible);
            const hidx = headerSideways.indexOf(focused);
            if (hidx > 0) { headerSideways[hidx - 1].focus({ preventScroll: true }); return; }
          }

          // Mirror of the ArrowRight row-wide step: move to the previous sideway button
          // anywhere in the same feed/flat row (e.g. context menu -> status badge).
          const row = focused.closest('.feedItem, .flatItem');
          if (row && sidebar!.contains(row)) {
            const rowSideways = Array.from(row.querySelectorAll<HTMLElement>('.sidewayFocus.flowFocussable')).filter(isVisible);
            const ridx = rowSideways.indexOf(focused);
            if (ridx > 0) { rowSideways[ridx - 1].focus({ preventScroll: true }); return; }
          }

          // Go to non-sideway sibling (walk up parent chain to handle wrapper divs)
          let searchParent = focused.parentElement;
          while (searchParent && sidebar!.contains(searchParent)) {
            const siblings = Array.from(searchParent.querySelectorAll<HTMLElement>(':scope > .flowFocussable:not(.sidewayFocus)'));
            if (siblings.length) { siblings[siblings.length - 1].focus({ preventScroll: true }); return; }
            searchParent = searchParent.parentElement;
          }

          // Go to ancestor non-sideway item
          const ancestor = focused.closest('.flowFocussable:not(.sidewayFocus)') as HTMLElement | null;
          if (ancestor && sidebar!.contains(ancestor)) {
            ancestor.focus({ preventScroll: true });
          }
          return;
        }

        // Footer buttons are flat siblings — step to the previous one if any.
        if (focused.classList.contains('sidebarFooterButton')) {
          const buttons = Array.from(focused.parentElement?.querySelectorAll<HTMLElement>(':scope > .sidebarFooterButton.flowFocussable') ?? []);
          const prev = buttons[buttons.indexOf(focused) - 1];
          if (prev) prev.focus({ preventScroll: true });
          return;
        }

        // Nothing to the left of a non-sideway item
        return;
      }
    }

    // Capture phase so we run before children's React-delegated handlers — in particular
    // the t-lib-ui <A> widget intercepts ArrowRight via its FlowForm key handler and
    // stops propagation, which would otherwise prevent this handler from firing on the
    // Discover link.
    document.addEventListener('keydown', handleKeyDown, true);
    sidebar.addEventListener('mousedown', mouseHandler);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      sidebar.removeEventListener('mousedown', mouseHandler);
    };
  }, [sidebarRef]);
}

/** Find nearest visible focusable element in any FlowForm that isn't the sidebar */
function findNearestInMainContent(sourceEl: HTMLElement): HTMLElement | null {
  const forms = Array.from(document.querySelectorAll('.flowForm:not(.leftSidebar)')) as HTMLElement[];
  const candidates: HTMLElement[] = [];
  for (let fi = 0; fi < forms.length; fi++) {
    const form = forms[fi];
    const rect = form.getBoundingClientRect();
    if (rect.width <= 0) continue;
    const els = Array.from(form.querySelectorAll('.flowFocussable:not(.sidewayFocus):not(.touchOnly)')) as HTMLElement[];
    for (let ei = 0; ei < els.length; ei++) {
      if (isVisible(els[ei]) && isPartiallyVisible(els[ei], rect)) candidates.push(els[ei]);
    }
  }
  if (!candidates.length) return null;

  const sourceRect = sourceEl.getBoundingClientRect();
  const sourceCenter = (sourceRect.top + sourceRect.bottom) / 2;
  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const el of candidates) {
    const r = el.getBoundingClientRect();
    const center = (r.top + r.bottom) / 2;
    const dist = Math.abs(center - sourceCenter);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  return best;
}

/** Find nearest fully-visible sidebar item at a similar vertical position to the source element */
export function findNearestSidebarItem(sourceEl: HTMLElement): HTMLElement | null {
  const sidebar = document.querySelector('.leftSidebar') as HTMLElement | null;
  if (!sidebar) return null;

  const sidebarRect = sidebar.getBoundingClientRect();
  if (sidebarRect.width <= 0) return null;

  const candidates = Array.from(sidebar.querySelectorAll<HTMLElement>('.flowFocussable:not(.sidewayFocus)'))
    .filter(el => isVisible(el) && isFullyVisible(el, sidebarRect));

  if (!candidates.length) return null;

  const sourceRect = sourceEl.getBoundingClientRect();
  const sourceCenter = (sourceRect.top + sourceRect.bottom) / 2;
  const sourceHCenter = (sourceRect.left + sourceRect.right) / 2;

  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const el of candidates) {
    const r = el.getBoundingClientRect();
    const center = (r.top + r.bottom) / 2;
    const hCenter = (r.left + r.right) / 2;
    // Vertical distance is dominant; horizontal is a tiebreaker for siblings on the same row (e.g. footer buttons).
    const dist = Math.abs(center - sourceCenter) * 1000 + Math.abs(hCenter - sourceHCenter);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  return best;
}

/** The page header's hamburger (drawer toggle), when visible — i.e. in narrow/mobile drawer mode. */
function findMenuButton(): HTMLElement | null {
  const btn = document.querySelector<HTMLElement>('button.menuOnMobile');
  return btn && isVisible(btn) ? btn : null;
}

/** LeaveForm handler: left goes to the nearest sidebar item; up — at the top of the content —
 *  reaches the page header's hamburger when it's showing (narrow/mobile drawer mode), so the
 *  drawer toggle is reachable by the primary up/down navigation rather than only sideways. */
export const sidebarLeaveHandler: LeaveFormHandler = (dir, from) => {
  if (dir == 'left') return findNearestSidebarItem(from) ?? undefined;
  if (dir == 'up') return findMenuButton() ?? undefined;
};
